// Splits a line of a dictation into the parts that became entries (with their
// teeth) and the rest, so the transcript can show what was taken from where.

export type Clause = { text: string; tooth: number };

export type Segment = { text: string; teeth: number[] };

type Range = { start: number; end: number; teeth: number[] };

/** The first place of `needle` in `lower` that no range has taken yet. */
function freePlace(lower: string, needle: string, taken: readonly Range[]): number {
  let from = 0;
  for (;;) {
    const start = lower.indexOf(needle, from);
    if (start < 0) return -1;
    const end = start + needle.length;
    if (!taken.some((r) => start < r.end && end > r.start)) return start;
    from = start + 1;
  }
}

/**
 * `clauses` are the sentences entries were parsed from. Each is found in the
 * line case-insensitively (first place not already taken); one clause that
 * produced several teeth ("16 i 17 karijes") is one segment with all of them.
 */
export function transcriptSegments(line: string, clauses: readonly Clause[]): Segment[] {
  const lower = line.toLowerCase();
  const ranges: Range[] = [];
  for (const clause of clauses) {
    const needle = clause.text.trim().toLowerCase();
    if (!needle) continue;
    const same = ranges.find((r) => lower.slice(r.start, r.end) === needle);
    if (same) {
      if (!same.teeth.includes(clause.tooth)) same.teeth.push(clause.tooth);
      continue;
    }
    const start = freePlace(lower, needle, ranges);
    if (start >= 0) ranges.push({ start, end: start + needle.length, teeth: [clause.tooth] });
  }
  ranges.sort((a, b) => a.start - b.start);

  const out: Segment[] = [];
  let at = 0;
  for (const r of ranges) {
    if (r.start > at) out.push({ text: line.slice(at, r.start), teeth: [] });
    out.push({ text: line.slice(r.start, r.end), teeth: [...r.teeth].sort((a, b) => a - b) });
    at = r.end;
  }
  if (at < line.length) out.push({ text: line.slice(at), teeth: [] });
  return out;
}
